// Phase 8C.6 — Artefact download card.
//
// Rendered inline when generate_artefact tool produces a PDF/image.
// Shows artefact type, preview, and download button.

import { Icon } from "../../../components/icons/Icon.tsx";

interface ArtefactData {
  artefact_id?: string;
  artefact_type?: string;
  title?: string;
  url?: string;
  file_size_bytes?: number;
  goal_title?: string;
  created_at?: string;
  error?: string;
}

const TYPE_LABELS: Record<string, { icon: string; label: string }> = {
  plan_summary: { icon: "document", label: "Plan Summary" },
  progress_report: { icon: "chart", label: "Progress Report" },
  milestone_certificate: { icon: "trophy", label: "Milestone Certificate" },
  goal_achieved: { icon: "star", label: "Achievement Certificate" },
};

function formatFileSize(bytes: number | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function SikaArtefactCard({ data }: { data: unknown }): JSX.Element {
  const d = (data ?? {}) as ArtefactData;

  if (d.error) {
    return (
      <div className="plan-card" style={{ borderColor: "var(--sika-negative, #C0392B)" }}>
        <div className="plan-head">
          <div className="label">Artefact Generation</div>
          <div className="ttl" style={{ color: "var(--sika-negative, #C0392B)" }}>Failed</div>
          <div className="meta">{d.error}</div>
        </div>
      </div>
    );
  }

  const typeInfo = TYPE_LABELS[d.artefact_type ?? ""] ?? { icon: "document", label: "Document" };
  const sizeStr = formatFileSize(d.file_size_bytes);

  return (
    <div className="plan-card">
      <div className="plan-head">
        <div className="label">{typeInfo.label}</div>
        <div className="ttl" style={{ fontFamily: "var(--sika-font-sans)", fontSize: "var(--sika-text-lg)" }}>
          {d.title ?? "Generated Document"}
        </div>
        {d.goal_title && <div className="meta">{d.goal_title}</div>}
      </div>

      <div className="plan-stats">
        <div className="stat-row">
          <span className="lbl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name={typeInfo.icon} size={12} />
            Type
          </span>
          <span className="val">{typeInfo.label}</span>
        </div>
        {sizeStr && (
          <div className="stat-row">
            <span className="lbl">Size</span>
            <span className="val">{sizeStr}</span>
          </div>
        )}
        {d.created_at && (
          <div className="stat-row">
            <span className="lbl">Generated</span>
            <span className="val">{new Date(d.created_at).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {d.url && (
        <div className="plan-disclosure" style={{ paddingTop: 12 }}>
          <a
            href={d.url}
            target="_blank"
            rel="noopener noreferrer"
            className="plan-action-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              background: "var(--sika-accent, #C5A028)",
              color: "var(--sika-bg, #0E0E10)",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "var(--sika-text-sm)",
            }}
          >
            <Icon name="download" size={14} />
            Download PDF
          </a>
        </div>
      )}
    </div>
  );
}
